import { useDroppable } from "@dnd-kit/core";
import React, { memo } from "react";

import { RegistryItem } from "../types";
import { useUnifiedRegistry } from "../unified-registry-context";

import { useDragDrop } from "./DragDropProvider";

import { EntityType, getEntityNumericId } from "@/queries/folder-entities/types";

interface DroppableFolderProps {
  item: RegistryItem;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

const DroppableFolder: React.FC<DroppableFolderProps> = memo(
  ({ item, children, disabled = false, className = "" }) => {
    const { draggedOverFolderId, descendantFolderIds } = useDragDrop();
    const registry = useUnifiedRegistry();
    const folderId = getEntityNumericId(item.id);

    const isDescendantOfActive = folderId !== undefined && descendantFolderIds.has(folderId);
    const isDisabled =
      disabled || registry.isSelected(item) || isDescendantOfActive || folderId === undefined;

    const { isOver, setNodeRef } = useDroppable({
      id: `folder-${folderId ?? item.id}`,
      disabled: isDisabled,
      data: {
        type: "folder",
        accepts: [
          EntityType.PROMPT,
          EntityType.SNIPPET,
          EntityType.WORKFLOW,
          EntityType.FOLDER,
          EntityType.DATASET,
          EntityType.REPORT,
          EntityType.AB_TEST,
          EntityType.INPUT_VARIABLE_SET,
          EntityType.SKILL_COLLECTION,
        ],
        folderId,
      },
    });

    const showDropFeedback =
      folderId !== undefined && isOver && !isDisabled && draggedOverFolderId === folderId;

    return (
      <div
        ref={setNodeRef}
        className={`
          relative
          ${className}
          ${showDropFeedback ? "scale-[0.95] " : ""}
          transition-all duration-200
        `}
      >
        {children}
        {showDropFeedback && (
          <div className="pointer-events-none absolute inset-0 rounded bg-blue-500 bg-opacity-10" />
        )}
      </div>
    );
  },
);

DroppableFolder.displayName = "DroppableFolder";
export default DroppableFolder;
